import { useContext, useEffect, useRef } from "react"
import SearchModalContext from "../../components/search/searchModalContext"

export default inputRef => {
  const { resultsListRef } = useContext(SearchModalContext)

  // Storing the focused result index with `useState` seems like it should work below,
  // but it actually results in stale state being referenced in our event handler.
  // We get around this by keeping the latest state inside a *ref*, mutating it,
  // and reading from that ref.
  // https://reactjs.org/docs/hooks-faq.html#why-am-i-seeing-stale-props-or-state-inside-my-function
  const focusedResultIdxRef = useRef(-1) // start index "outside" the results list

  // visible results could be a list on either tab, or recently viewed results
  const getVisibleResults = listRef =>
    Array.from(listRef.current?.querySelectorAll("a") || [])

  const nextIdx = (idx, resultsLength) =>
    idx === resultsLength - 1 ? 0 : idx + 1
  const prevIdx = (idx, resultsLength) =>
    idx === 0 ? resultsLength - 1 : idx - 1

  const handleKeyDown = e => {
    // tab, enter, and shift have default behaviours we don't want to mess with
    if (e.key === "Tab" || e.key === "Enter" || e.key === "Shift") return

    // if the user types a search query (i.e. any other key), return focus
    // back to the input field
    if (e.key !== "ArrowDown" && e.key !== "ArrowUp") {
      inputRef.current?.focus()
      focusedResultIdxRef.current = -1
    }

    // handle arrow keys and loop navigation at list boundaries
    const visibleResults = getVisibleResults(resultsListRef)

    if (e.key === "ArrowDown") {
      focusedResultIdxRef.current = nextIdx(
        focusedResultIdxRef.current,
        visibleResults.length
      )
    } else if (e.key === "ArrowUp" && focusedResultIdxRef.current >= 0) {
      focusedResultIdxRef.current = prevIdx(
        focusedResultIdxRef.current,
        visibleResults.length
      )
    }
    visibleResults?.[focusedResultIdxRef.current]?.focus()
  }

  const handleTabPress = e => {
    // ensure tab navigation still works per default browser behaviour,
    // while updating the user's place in the list for arrow navigtion
    if (e.key === "Tab") {
      const visibleResults = getVisibleResults(resultsListRef)
      const focusedResultIdx = visibleResults.indexOf(document.activeElement)
      focusedResultIdxRef.current = focusedResultIdx
    }
  }

  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown)
    document.addEventListener("keyup", handleTabPress)
    return () => {
      document.removeEventListener("keydown", handleKeyDown)
      document.removeEventListener("keyup", handleTabPress)
    }
  }, [handleKeyDown, handleTabPress])
}
